import Papa from 'papaparse'
import type { CellValue } from 'read-excel-file/browser'
import { Ops } from '@willie/schemas'
import type { TRPCOutputs } from '~/utils/trpc'
import { emptySite, emptyStation, existingSite, type SiteDraft, type StationDraft } from './batchDraft'
import { STATION_TYPE_OPTIONS, type StationType } from './constants'

const { normalizeName, BATCH_MAX_INSTALLATIONS, BATCH_MAX_SITES } = Ops.StationInstallations

const CSV_HEADERS = [
  'site_name',
  'site_contact_name',
  'site_contact_email',
  'site_contact_phone',
  'site_contact_address',
  'installation_contact_name',
  'installation_contact_email',
  'installation_contact_phone',
  'station_name',
  'station_type',
  'station_internal_id',
  'picture_from_client_required'
] as const

type CsvHeader = (typeof CSV_HEADERS)[number]

export interface RawRow {
  /** As a spreadsheet numbers it: the header is row 1. */
  rowNumber: number
  values: Record<CsvHeader, string>
}

export interface ParseOutcome {
  rows: RawRow[]
  fatal: string[]
}

export interface CsvIssue {
  rowNumber: number
  column: CsvHeader
  message: string
}

type OrgSite = TRPCOutputs['ops']['sites']['list'][number]
type OrgContact = TRPCOutputs['ops']['contacts']['list'][number]
export type SiteStation = TRPCOutputs['ops']['stationInstallations']['listSiteStations'][number]

const HEADER_ROW_OFFSET = 2

// Excel prepends a BOM and people type headers with spaces, so both are forgiven.
const normalizeHeader = (header: string): string =>
  header
    .replace(/^\uFEFF/, '')
    .trim()
    .toLowerCase()
    .replace(/\s+/g, '_')

/** What both formats come down to: a header row, then one record per spreadsheet row. */
const toParseOutcome = (headers: string[], records: string[][]): ParseOutcome => {
  const columns = headers.map(normalizeHeader)
  // Numbered before blank rows drop out, so a row number still points at the right spreadsheet row.
  const filled = records
    .map((record, index) => ({ record, rowNumber: index + HEADER_ROW_OFFSET }))
    .filter(({ record }) => record.some((cell) => cell.trim() !== ''))

  const siteColumn = columns.indexOf('site_name')
  const siteCount = new Set(
    filled.map(({ record }) => normalizeName(record[siteColumn] ?? '')).filter((name) => name !== '')
  ).size
  const fatal = [
    ...(siteColumn === -1 ? ['Missing required column: site_name'] : []),
    ...(filled.length === 0 ? ['The file has headers but no data rows'] : []),
    ...(filled.length > BATCH_MAX_INSTALLATIONS
      ? [`Too many rows: ${filled.length} (max ${BATCH_MAX_INSTALLATIONS}) — split the file into several batches`]
      : []),
    ...(siteCount > BATCH_MAX_SITES
      ? [`Too many sites: ${siteCount} (max ${BATCH_MAX_SITES}) — split the file into several batches`]
      : [])
  ]
  if (fatal.length > 0) return { rows: [], fatal }

  const positions = CSV_HEADERS.map((h) => [h, columns.indexOf(h)] as const)
  const rows = filled.map(({ record, rowNumber }) => ({
    rowNumber,
    values: Object.fromEntries(positions.map(([h, at]) => [h, (record[at] ?? '').trim()])) as RawRow['values']
  }))
  return { rows, fatal: [] }
}

export const parseCsv = (text: string): ParseOutcome => {
  if (text.trim() === '') return { rows: [], fatal: ['CSV is empty'] }

  const parsed = Papa.parse<string[]>(text)
  // A one-column file has no delimiter to find, and Papa reports that as an error.
  const errors = parsed.errors
    .filter((e) => e.code !== 'UndetectableDelimiter')
    .map((e) => (e.row === undefined ? e.message : `Row ${e.row + 1}: ${e.message}`))
  if (errors.length > 0) return { rows: [], fatal: errors }

  const [headers = [], ...records] = parsed.data
  return toParseOutcome(headers, records)
}

// Excel keeps a number as typed digits only when the cell is Text, so a leading zero is already
// gone by the time we read it. Taking the stored digits as-is at least spares float noise.
const cellText = (cell: CellValue<string> | null): string => {
  if (cell === null) return ''
  if (cell instanceof Date) return cell.toISOString().slice(0, 10)
  if (typeof cell === 'boolean') return cell ? 'yes' : 'no'
  return String(cell)
}

/** The first sheet of an .xlsx. The reader is loaded on first use, so the wizard does not ship it. */
export const parseSpreadsheet = async (file: File): Promise<ParseOutcome> => {
  try {
    const { readSheet } = await import('read-excel-file/browser')
    const sheet = await readSheet<string>(file, { parseNumber: (digits) => digits })
    const [headers = [], ...records] = sheet.map((row) => row.map(cellText))
    return headers.length === 0 ? { rows: [], fatal: ['The spreadsheet is empty'] } : toParseOutcome(headers, records)
  } catch (err) {
    return { rows: [], fatal: [`Could not read this spreadsheet: ${err instanceof Error ? err.message : String(err)}`] }
  }
}

// ---------------------------------------------------------------------------
// Sites step
// ---------------------------------------------------------------------------

// Only the shipping contact has an address: it is where the hardware is delivered.
const CONTACT_ROLES = {
  siteContactId: {
    label: 'shipping contact',
    name: 'site_contact_name',
    email: 'site_contact_email',
    phone: 'site_contact_phone',
    address: 'site_contact_address'
  },
  installationContactId: {
    label: 'installation contact',
    name: 'installation_contact_name',
    email: 'installation_contact_email',
    phone: 'installation_contact_phone'
  }
} as const satisfies Record<
  keyof Pick<SiteDraft, 'siteContactId' | 'installationContactId'>,
  { label: string; name: CsvHeader; email: CsvHeader; phone: CsvHeader; address?: CsvHeader }
>

type ContactRole = keyof typeof CONTACT_ROLES

interface CsvContact {
  key: string
  rowNumber: number
  column: CsvHeader
  fields: Ops.Contacts.ContactFields
}

const emailSchema = Ops.Contacts.contactFieldsSchema.shape.email

// One person, however the rows spell them: by email when there is one, by name otherwise.
const contactKey = (email: string | null, name: string): string =>
  email ? `email:${email.toLowerCase()}` : `name:${normalizeName(name)}`

const readContact = (row: RawRow, role: ContactRole): CsvContact | CsvIssue | null => {
  const columns = CONTACT_ROLES[role]
  const name = row.values[columns.name]
  const email = row.values[columns.email]
  const phone = row.values[columns.phone]
  const address = 'address' in columns ? row.values[columns.address] : ''
  if (!name && !email && !phone && !address) return null
  if (email && !emailSchema.safeParse(email).success) {
    return { rowNumber: row.rowNumber, column: columns.email, message: `Not a valid email: ${email}` }
  }
  const displayName = name || email
  if (!displayName) {
    return { rowNumber: row.rowNumber, column: columns.name, message: `The ${columns.label} needs a name or an email` }
  }
  return {
    key: contactKey(email || null, displayName),
    rowNumber: row.rowNumber,
    column: email ? columns.email : columns.name,
    fields: { name: displayName, email: email || null, phone: phone || null, address: address || null }
  }
}

const sameAddress = (a: string, b: string): boolean => normalizeName(a) === normalizeName(b)

/** One contact seen twice: a blank address takes the other's, two different ones are reported. */
const mergeContact = (known: CsvContact, incoming: CsvContact): { contact: CsvContact; issues: CsvIssue[] } => {
  const knownAddress = known.fields.address
  const incomingAddress = incoming.fields.address
  if (!incomingAddress || (knownAddress && sameAddress(knownAddress, incomingAddress))) {
    return { contact: known, issues: [] }
  }
  if (!knownAddress) return { contact: { ...known, fields: { ...known.fields, address: incomingAddress } }, issues: [] }
  return {
    contact: known,
    issues: [
      {
        rowNumber: incoming.rowNumber,
        column: 'site_contact_address',
        message: `${known.fields.name} has a different address on row ${known.rowNumber} — row ${known.rowNumber} is used`
      }
    ]
  }
}

/** The one contact a site's rows agree on for a role; a second, different one is reported, not merged. */
const siteContact = (rows: RawRow[], role: ContactRole): { contact: CsvContact | null; issues: CsvIssue[] } =>
  rows.reduce<{ contact: CsvContact | null; issues: CsvIssue[] }>(
    (acc, row) => {
      const read = readContact(row, role)
      if (read === null) return acc
      if ('message' in read) return { ...acc, issues: [...acc.issues, read] }
      if (acc.contact === null) return { ...acc, contact: read }
      if (acc.contact.key === read.key) {
        const merged = mergeContact(acc.contact, read)
        return { contact: merged.contact, issues: [...acc.issues, ...merged.issues] }
      }
      const first = acc.contact.rowNumber
      const issue = {
        rowNumber: row.rowNumber,
        column: CONTACT_ROLES[role].name,
        message: `Different ${CONTACT_ROLES[role].label} than row ${first} for the same site — row ${first} is used`
      }
      return { ...acc, issues: [...acc.issues, issue] }
    },
    { contact: null, issues: [] }
  )

// Under its name too, so a row that gives only a name still finds a contact the org holds an email for.
const existingContactKeys = (contact: OrgContact): string[] => [
  ...(contact.email ? [contactKey(contact.email, contact.name)] : []),
  contactKey(null, contact.name)
]

/** Every contact the file names, once each, however many sites share it. */
const mergeAcrossSites = (contacts: CsvContact[]): { byKey: Map<string, CsvContact>; issues: CsvIssue[] } =>
  contacts.reduce<{ byKey: Map<string, CsvContact>; issues: CsvIssue[] }>(
    (acc, contact) => {
      const known = acc.byKey.get(contact.key)
      const merged = known ? mergeContact(known, contact) : { contact, issues: [] }
      return { byKey: acc.byKey.set(contact.key, merged.contact), issues: [...acc.issues, ...merged.issues] }
    },
    { byKey: new Map(), issues: [] }
  )

/** A matched contact takes the file's address only when it has none; an address it holds is never overwritten. */
const addressChange = (
  contact: CsvContact,
  existing: OrgContact
): { updates: Ops.Contacts.UpdateContactInput[]; issues: CsvIssue[] } => {
  const address = contact.fields.address
  if (!address || (existing.address && sameAddress(existing.address, address))) return { updates: [], issues: [] }
  if (!existing.address) return { updates: [{ contactId: existing.id, address }], issues: [] }
  return {
    updates: [],
    issues: [
      {
        rowNumber: contact.rowNumber,
        column: 'site_contact_address',
        message: `${existing.name} already has the address ${existing.address} — edit the contact to change it`
      }
    ]
  }
}

// Appends in place: copying each group per item is quadratic, and one site can hold every row.
const groupBy = <T>(items: T[], keysOf: (item: T) => string[]): Map<string, T[]> =>
  items.reduce((acc, item) => {
    keysOf(item).forEach((key) => {
      const group = acc.get(key)
      if (group) group.push(item)
      else acc.set(key, [item])
    })
    return acc
  }, new Map<string, T[]>())

const groupByName = <T>(items: T[], name: (item: T) => string): Map<string, T[]> =>
  groupBy(items, (item) => [normalizeName(name(item))])

const missingSiteNameIssues = (rows: RawRow[]): CsvIssue[] =>
  rows
    .filter((row) => row.values.site_name === '')
    .map((row) => ({ rowNumber: row.rowNumber, column: 'site_name', message: 'site_name is empty' }))

/**
 * Each distinct site_name as a site draft — ticking the org's site of that name, or naming a new one.
 *
 * Contacts are matched against the org's by email, else by name. The unmatched ones come back in
 * `contactsToCreate`, and matched ones missing the file's address in `contactsToUpdate`: apply both,
 * then call this again, and every site carries an id.
 */
export const toSiteDrafts = (
  rows: RawRow[],
  orgSites: OrgSite[],
  contacts: OrgContact[]
): {
  sites: SiteDraft[]
  contactsToCreate: Ops.Contacts.ContactFields[]
  contactsToUpdate: Ops.Contacts.UpdateContactInput[]
  issues: CsvIssue[]
} => {
  const orgContactsByKey = groupBy(contacts, existingContactKeys)
  const matchesOf = (contact: CsvContact): OrgContact[] => orgContactsByKey.get(contact.key) ?? []
  // Two of the org's contacts behind one key are not told apart by guessing: the row is reported instead.
  const soleMatchOf = (contact: CsvContact): OrgContact | null => {
    const matches = matchesOf(contact)
    return matches.length === 1 ? matches[0] : null
  }
  const idFor = (contact: CsvContact | null): string | null => (contact ? (soleMatchOf(contact)?.id ?? null) : null)
  const orgSitesByName = groupByName(orgSites, (site) => site.name)

  const planned = [...groupByName(rows.filter((row) => row.values.site_name !== ''), (row) => row.values.site_name).values()].map(
    (siteRows) => {
      const [first] = siteRows
      const matches = orgSitesByName.get(normalizeName(first.values.site_name)) ?? []
      const shipping = siteContact(siteRows, 'siteContactId')
      const installation = siteContact(siteRows, 'installationContactId')
      const ambiguous: CsvIssue[] =
        matches.length > 1
          ? [
              {
                rowNumber: first.rowNumber,
                column: 'site_name',
                message: `${matches.length} existing sites are named ${first.values.site_name} — tick the right one by hand`
              }
            ]
          : []
      return {
        draft:
          matches.length === 1
            ? existingSite(matches[0].id, matches[0].name)
            : matches.length === 0
              ? { ...emptySite(), siteName: first.values.site_name }
              : null,
        contacts: { siteContactId: shipping.contact, installationContactId: installation.contact },
        issues: [...ambiguous, ...shipping.issues, ...installation.issues]
      }
    }
  )

  const fileContacts = mergeAcrossSites(
    planned
      .flatMap((p) => [p.contacts.siteContactId, p.contacts.installationContactId])
      .filter((c): c is CsvContact => c !== null)
  )
  const merged = [...fileContacts.byKey.values()]
  const addressChanges = merged.flatMap((c) => {
    const existing = soleMatchOf(c)
    return existing ? [addressChange(c, existing)] : []
  })
  const ambiguousContacts = merged.flatMap((c): CsvIssue[] => {
    const count = matchesOf(c).length
    return count > 1
      ? [
          {
            rowNumber: c.rowNumber,
            column: c.column,
            message: `${count} of the organization's contacts match ${c.fields.email ?? c.fields.name} — pick the right one by hand`
          }
        ]
      : []
  })

  return {
    sites: planned.flatMap((p) =>
      p.draft
        ? [
            {
              ...p.draft,
              siteContactId: idFor(p.contacts.siteContactId),
              installationContactId: idFor(p.contacts.installationContactId)
            }
          ]
        : []
    ),
    contactsToCreate: merged.filter((c) => matchesOf(c).length === 0).map((c) => c.fields),
    contactsToUpdate: addressChanges.flatMap((change) => change.updates),
    issues: [
      ...missingSiteNameIssues(rows),
      ...planned.flatMap((p) => p.issues),
      ...fileContacts.issues,
      ...ambiguousContacts,
      ...addressChanges.flatMap((change) => change.issues)
    ]
  }
}

// ---------------------------------------------------------------------------
// Stations step
// ---------------------------------------------------------------------------

const TRUE_VALUES = ['yes', 'y', 'true', '1', 'oui', 'o']
const FALSE_VALUES = ['no', 'n', 'false', '0', 'non']

const readPictureRequired = (row: RawRow): { value: boolean; issues: CsvIssue[] } => {
  const raw = row.values.picture_from_client_required.toLowerCase()
  if (TRUE_VALUES.includes(raw)) return { value: true, issues: [] }
  if (raw === '' || FALSE_VALUES.includes(raw)) return { value: false, issues: [] }
  return {
    value: false,
    issues: [
      {
        rowNumber: row.rowNumber,
        column: 'picture_from_client_required',
        message: `Expected yes or no, got ${row.values.picture_from_client_required}`
      }
    ]
  }
}

const readStationType = (row: RawRow): { value: StationType | ''; issues: CsvIssue[] } => {
  const raw = row.values.station_type
  const value = STATION_TYPE_OPTIONS.find((option) => option === raw.toLowerCase())
  if (value) return { value, issues: [] }
  const message = raw === '' ? 'station_type is empty' : `Expected one of ${STATION_TYPE_OPTIONS.join(', ')}, got ${raw}`
  return { value: '', issues: [{ rowNumber: row.rowNumber, column: 'station_type', message }] }
}

type StationMatch = { station: SiteStation } | { issue: string } | null

const matchesRow = (row: RawRow, station: SiteStation): boolean => {
  const internalId = row.values.station_internal_id.toLowerCase()
  return internalId
    ? station.internal_id?.toLowerCase() === internalId
    : normalizeName(station.name) === normalizeName(row.values.station_name)
}

const soleMatch = (candidates: SiteStation[], label: string): StationMatch => {
  if (candidates.length === 1) return { station: candidates[0] }
  if (candidates.length > 1) return { issue: `${candidates.length} stations in this site match ${label} — pick one by hand` }
  return null
}

/**
 * An existing station of the site this row names: by internal id when the row gives one, else by name.
 * A row's internal id is never overridden by a name — it names a meter, possibly one still to create.
 */
const matchStation = (row: RawRow, available: SiteStation[]): StationMatch => {
  const internalId = row.values.station_internal_id
  const name = normalizeName(row.values.station_name)
  if (!internalId && !name) return null
  return soleMatch(
    available.filter((s) => matchesRow(row, s)),
    internalId ? `internal id ${internalId}` : `name ${row.values.station_name}`
  )
}

// The picker leaves out a station that already has an installation or is taken by another row, so a
// row naming one drafts a new station — which the site refuses when it carries the same internal id.
const unavailableStationIssues = (row: RawRow, station: SiteStation | undefined, adopted: Set<string>): CsvIssue[] => {
  if (!station) return []
  const why = adopted.has(station.id)
    ? `${station.name} is already used by another row of this file`
    : `${station.name} already has installation #${station.already_linked_to}`
  const byInternalId = row.values.station_internal_id !== ''
  return [
    {
      rowNumber: row.rowNumber,
      column: byInternalId ? 'station_internal_id' : 'station_name',
      message: byInternalId
        ? `${why}, so a new station with this internal id will be refused — clear station_internal_id`
        : `${why}, so a new station is drafted`
    }
  ]
}

interface StationPlan {
  stations: StationDraft[]
  issues: CsvIssue[]
  adopted: Set<string>
}

const planStation = (
  acc: StationPlan,
  row: RawRow,
  site: SiteDraft,
  siteStations: SiteStation[]
): StationPlan => {
  const picture = readPictureRequired(row)
  const shared = { pictureFromClientRequired: picture.value }

  const available = siteStations.filter((s) => s.already_linked_to === null && !acc.adopted.has(s.id))
  const match = matchStation(row, available)
  if (match && 'station' in match) {
    const adopted: StationDraft = {
      ...emptyStation(site.key),
      ...shared,
      mode: 'adopt',
      waterStationId: match.station.id
    }
    return {
      stations: [...acc.stations, adopted],
      issues: [...acc.issues, ...picture.issues],
      adopted: new Set([...acc.adopted, match.station.id])
    }
  }

  const unavailable = match
    ? undefined
    : siteStations.find((s) => (s.already_linked_to !== null || acc.adopted.has(s.id)) && matchesRow(row, s))
  const ambiguity: CsvIssue[] =
    match && 'issue' in match ? [{ rowNumber: row.rowNumber, column: 'station_name', message: match.issue }] : []
  const stationType = readStationType(row)
  const nameIssue: CsvIssue[] = row.values.station_name
    ? []
    : [{ rowNumber: row.rowNumber, column: 'station_name', message: 'station_name is empty' }]
  const created: StationDraft = {
    ...emptyStation(site.key),
    ...shared,
    stationName: row.values.station_name,
    stationType: stationType.value,
    internalId: row.values.station_internal_id
  }
  return {
    stations: [...acc.stations, created],
    issues: [
      ...acc.issues,
      ...ambiguity,
      ...unavailableStationIssues(row, unavailable, acc.adopted),
      ...nameIssue,
      ...stationType.issues,
      ...picture.issues
    ],
    adopted: acc.adopted
  }
}

/**
 * One station draft per row whose site is in the batch. A row matching exactly one unlinked station
 * of an existing site adopts it; any other row creates a station. Rows that cannot be read are still
 * drafted, so the wizard's own validation holds them until they are fixed.
 */
export const toStationDrafts = (
  rows: RawRow[],
  sites: SiteDraft[],
  siteStations: Record<string, SiteStation[]>
): { stations: StationDraft[]; issues: CsvIssue[] } => {
  const siteByName = new Map(sites.map((site) => [normalizeName(site.siteName), site]))

  const plan = rows.reduce<StationPlan>(
    (acc, row) => {
      if (row.values.site_name === '') return acc
      const site = siteByName.get(normalizeName(row.values.site_name))
      if (!site) {
        const issue: CsvIssue = {
          rowNumber: row.rowNumber,
          column: 'site_name',
          message: `${row.values.site_name} is not in this batch — add it in the Sites step`
        }
        return { ...acc, issues: [...acc.issues, issue] }
      }
      return planStation(acc, row, site, site.siteId ? (siteStations[site.siteId] ?? []) : [])
    },
    { stations: [], issues: missingSiteNameIssues(rows), adopted: new Set() }
  )

  return { stations: plan.stations, issues: plan.issues }
}
